import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Tags, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmModal from '../../ui/ConfirmModal';
import { gymDarkCard } from '../gymPageTheme';

const fieldClassName =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/50';

const labelClassName = 'mb-1 block text-sm font-medium text-gray-700 dark:text-white/85';

const emptyForm = () => ({
    name: '',
    subscription_type: 'monthly',
    price: '',
    description: '',
    is_active: true,
});

function formatPrice(price) {
    const n = Number(price);
    if (Number.isNaN(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export default function GymPriceList({ isDark }) {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const typeLabel = useCallback(
        (type) => t(`gym.subscriptionModal.subscriptionTypes.${type}`, type),
        [t]
    );

    const loadItems = useCallback(async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_subscription_price_list.php?action=list&include_inactive=1');
            if (res.data?.success) {
                setItems(Array.isArray(res.data.items) ? res.data.items : []);
            } else {
                toast.error(res.data?.error || t('gym.billing.priceList.loadError'));
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(err.response?.data?.error || t('gym.billing.priceList.loadError'));
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, t]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setShowForm(true);
    };

    const openEdit = (item) => {
        setEditingId(item.id);
        setForm({
            name: item.name || '',
            subscription_type: item.subscription_type || 'monthly',
            price: item.price != null && Number(item.price) > 0 ? String(item.price) : item.price === 0 ? '0' : '',
            description: item.description || '',
            is_active: item.is_active !== false,
        });
        setShowForm(true);
    };

    const closeForm = () => {
        if (saving) return;
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error(t('gym.billing.priceList.nameRequired'));
            return;
        }
        try {
            setSaving(true);
            const api = getAuthenticatedApi();
            const payload = {
                name: form.name.trim(),
                subscription_type: form.subscription_type,
                price: form.price === '' ? 0 : parseFloat(form.price),
                description: form.description.trim(),
                is_active: form.is_active,
            };
            const endpoint = editingId
                ? '/gym_subscription_price_list.php?action=update'
                : '/gym_subscription_price_list.php?action=create';
            if (editingId) payload.id = editingId;

            const res = await api.post(endpoint, payload);
            if (!res.data?.success) {
                toast.error(res.data?.error || t('gym.billing.priceList.saveError'));
                return;
            }
            toast.success(
                editingId
                    ? t('gym.billing.priceList.updateSuccess')
                    : t('gym.billing.priceList.createSuccess')
            );
            closeForm();
            await loadItems();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(err.response?.data?.error || t('gym.billing.priceList.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            const api = getAuthenticatedApi();
            const res = await api.post('/gym_subscription_price_list.php?action=delete', {
                id: deleteTarget.id,
                hard: true,
            });
            if (!res.data?.success) {
                toast.error(res.data?.error || t('gym.billing.priceList.deleteError'));
                return;
            }
            toast.success(t('gym.billing.priceList.deleteSuccess'));
            setDeleteTarget(null);
            await loadItems();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(err.response?.data?.error || t('gym.billing.priceList.deleteError'));
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <section className="space-y-4">
            <div
                className={`rounded-xl border p-4 sm:p-5 ${
                    isDark ? `${gymDarkCard} border-white/10 text-white` : 'bg-white border-gray-200 text-gray-900'
                }`}
            >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex gap-3">
                        <Tags className={`h-6 w-6 shrink-0 mt-0.5 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`} />
                        <div>
                            <h2 className="text-base font-semibold">{t('gym.billing.priceList.title')}</h2>
                            <p className="text-xs mt-1 opacity-70 max-w-xl">{t('gym.billing.priceList.description')}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                        <Plus className="h-4 w-4" />
                        {t('gym.billing.priceList.add')}
                    </button>
                </div>
            </div>

            {showForm && (
                <div
                    className={`rounded-xl border p-4 sm:p-5 ${
                        isDark ? `${gymDarkCard} border-indigo-500/30 text-white` : 'bg-white border-indigo-200 text-gray-900'
                    }`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold">
                            {editingId
                                ? t('gym.billing.priceList.editTitle')
                                : t('gym.billing.priceList.createTitle')}
                        </h3>
                        <button
                            type="button"
                            onClick={closeForm}
                            className="rounded-md p-1 opacity-70 hover:opacity-100"
                            aria-label={t('common.cancel', 'Annulla')}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label className={labelClassName}>{t('gym.billing.priceList.fields.name')}</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                className={fieldClassName}
                                placeholder={t('gym.billing.priceList.fields.namePlaceholder')}
                                required
                            />
                        </div>
                        <div>
                            <label className={labelClassName}>{t('gym.billing.priceList.fields.type')}</label>
                            <select
                                value={form.subscription_type}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, subscription_type: e.target.value }))
                                }
                                className={fieldClassName}
                            >
                                <option value="monthly">{typeLabel('monthly')}</option>
                                <option value="quarterly">{typeLabel('quarterly')}</option>
                                <option value="yearly">{typeLabel('yearly')}</option>
                                <option value="custom">{typeLabel('custom')}</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClassName}>{t('gym.billing.priceList.fields.price')}</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.price}
                                onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                                className={fieldClassName}
                                placeholder={t('gym.billing.priceList.fields.pricePlaceholder')}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={labelClassName}>{t('gym.billing.priceList.fields.description')}</label>
                            <input
                                type="text"
                                value={form.description}
                                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                                className={fieldClassName}
                                placeholder={t('gym.billing.priceList.fields.descriptionPlaceholder')}
                            />
                        </div>
                        {editingId && (
                            <div className="sm:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={form.is_active}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                                        }
                                        className="rounded border-gray-300"
                                    />
                                    {t('gym.billing.priceList.fields.active')}
                                </label>
                            </div>
                        )}
                        <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={closeForm}
                                disabled={saving}
                                className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-white/90"
                            >
                                {t('common.cancel', 'Annulla')}
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {saving
                                    ? t('gym.billing.priceList.saving')
                                    : editingId
                                      ? t('gym.billing.priceList.save')
                                      : t('gym.billing.priceList.create')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {items.length === 0 ? (
                <div
                    className={`rounded-xl border border-dashed p-8 text-center ${
                        isDark
                            ? `${gymDarkCard} border-white/20 text-white`
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <Tags className="h-10 w-10 mx-auto opacity-40 mb-3" />
                    <p className="text-sm font-semibold">{t('gym.billing.priceList.emptyTitle')}</p>
                    <p className="text-xs mt-2 max-w-md mx-auto opacity-70">
                        {t('gym.billing.priceList.emptyBody')}
                    </p>
                </div>
            ) : (
                <div
                    className={`rounded-xl border overflow-hidden ${
                        isDark ? `${gymDarkCard} border-white/10` : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead
                                className={
                                    isDark
                                        ? 'bg-gray-800/80 text-white/70'
                                        : 'bg-gray-50 text-gray-600'
                                }
                            >
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium">
                                        {t('gym.billing.priceList.columns.name')}
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium">
                                        {t('gym.billing.priceList.columns.type')}
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium">
                                        {t('gym.billing.priceList.columns.price')}
                                    </th>
                                    <th className="hidden px-4 py-3 text-left font-medium sm:table-cell">
                                        {t('gym.billing.priceList.columns.status')}
                                    </th>
                                    <th className="px-4 py-3 text-right font-medium">
                                        {t('gym.billing.priceList.columns.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={isDark ? 'text-white' : 'text-gray-900'}>
                                {items.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={`border-t ${
                                            isDark ? 'border-white/10' : 'border-gray-100'
                                        }`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.name}</div>
                                            {item.description && (
                                                <div className="text-xs opacity-60 mt-0.5">{item.description}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{typeLabel(item.subscription_type)}</td>
                                        <td className="px-4 py-3 tabular-nums">€{formatPrice(item.price)}</td>
                                        <td className="hidden px-4 py-3 sm:table-cell">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    item.is_active
                                                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                                                }`}
                                            >
                                                {item.is_active
                                                    ? t('gym.billing.priceList.statusActive')
                                                    : t('gym.billing.priceList.statusInactive')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(item)}
                                                    className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    title={t('common.edit', 'Modifica')}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDeleteTarget(item)}
                                                    className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                                                    title={t('common.delete', 'Elimina')}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title={t('gym.billing.priceList.deleteConfirmTitle')}
                message={t('gym.billing.priceList.deleteConfirmBody', {
                    name: deleteTarget?.name || '',
                })}
                confirmText={t('common.delete', 'Elimina')}
                cancelText={t('common.cancel', 'Annulla')}
                variant="warning"
                loading={deleting}
            />
        </section>
    );
}
